/**
 * The one mapping from `useFilterPanelLogic` to the Watchlist filter
 * sections (SE-8608).
 *
 * The Watchlist `FilterSheetContainer` and the pipeline's
 * `AddPlayersFilterPanelContainer` both call this, so a field wired here is
 * wired on both surfaces — the containers cannot drift the way their two
 * hand-written prop lists did.
 * @module features/scouting/utils/toFilterSheetSectionsProps
 */

import type { FilterOption } from "@/features/scouting/components/molecules/FilterChip";
import type useFilterPanelLogic from "@/features/scouting/hooks/useFilterPanelLogic";

import type { FilterSheetSectionsProps } from "@/features/scouting/components/organisms/FilterSheetSections";

/** What `useFilterPanelLogic` hands a filter surface. */
export type FilterPanelLogic = ReturnType<typeof useFilterPanelLogic>;

/** The host-supplied inputs the panel logic does not own. */
export interface FilterSheetSectionsSource {
  readonly discoveryOnly?: boolean;
  readonly sportingOnly?: boolean;
  readonly positionOptions: readonly FilterOption[];
  readonly clubOptions: readonly FilterOption[];
  readonly nationalityOptions: readonly FilterOption[];
  readonly leagueOptions: readonly FilterOption[];
  readonly roleOptions: readonly FilterOption[];
  readonly optionsLoading: boolean;
  readonly onServerSearchClub?: (text: string) => void;
  readonly clubSearching?: boolean;
  readonly customMetrics: FilterSheetSectionsProps["customMetrics"];
}

/**
 * Bind the panel logic's draft, section and chip state to the sections.
 * @param panelLogic - The surface's `useFilterPanelLogic` result.
 * @param source - Options and host flags the panel logic does not own.
 * @returns Every prop {@link FilterSheetSectionsProps} requires.
 */
export const toFilterSheetSectionsProps = (
  panelLogic: FilterPanelLogic,
  source: FilterSheetSectionsSource
): FilterSheetSectionsProps => ({
  discoveryOnly: source.discoveryOnly,
  sportingOnly: source.sportingOnly,
  optionsLoading: source.optionsLoading,
  /* --- Basic section --- */
  isBasicOpen: panelLogic.expandedSections.basic,
  onToggleBasic: panelLogic.onToggleBasic,
  basicFilterCount: panelLogic.sectionCounts.basic,
  positionOptions: source.positionOptions,
  selectedPositionIds: panelLogic.draft.selectedPositionIds,
  onTogglePosition: panelLogic.handleTogglePosition,
  onClearPositions: panelLogic.handleClearPositions,
  isPositionFilterOpen: panelLogic.openChipId === "position",
  onTogglePositionFilter: panelLogic.onTogglePositionFilter,
  onClosePositionFilter: panelLogic.onCloseChip,
  clubOptions: source.clubOptions,
  selectedClubIds: panelLogic.draft.selectedClubIds,
  onToggleClub: panelLogic.handleToggleClub,
  onClearClubs: panelLogic.handleClearClubs,
  isClubFilterOpen: panelLogic.openChipId === "club",
  onToggleClubFilter: panelLogic.onToggleClubFilter,
  onCloseClubFilter: panelLogic.onCloseChip,
  onServerSearchClub: source.onServerSearchClub,
  clubSearching: source.clubSearching,
  nationalityOptions: source.nationalityOptions,
  selectedNationalityIds: panelLogic.draft.selectedNationalityIds,
  onToggleNationality: panelLogic.handleToggleNationality,
  onClearNationalities: panelLogic.handleClearNationalities,
  isNationalityFilterOpen: panelLogic.openChipId === "nationality",
  onToggleNationalityFilter: panelLogic.onToggleNationalityFilter,
  onCloseNationalityFilter: panelLogic.onCloseChip,
  leagueOptions: source.leagueOptions,
  selectedLeagueIds: panelLogic.draft.selectedLeagueIds,
  onToggleLeague: panelLogic.handleToggleLeague,
  onClearLeagues: panelLogic.handleClearLeagues,
  isLeagueFilterOpen: panelLogic.openChipId === "league",
  onToggleLeagueFilter: panelLogic.onToggleLeagueFilter,
  onCloseLeagueFilter: panelLogic.onCloseChip,
  roleOptions: source.roleOptions,
  selectedRoleArchetypes: panelLogic.draft.selectedRoleArchetypes,
  onToggleRole: panelLogic.handleToggleRole,
  onClearRoles: panelLogic.handleClearRoles,
  ageRange: panelLogic.draft.ageRange,
  onSetAgeRange: panelLogic.handleSetAgeRange,
  contractMonthsRange: panelLogic.draft.contractMonthsRange,
  onSetContractMonthsRange: panelLogic.handleSetContractMonthsRange,
  /* --- Financial section --- */
  isFinancialOpen: panelLogic.expandedSections.financial,
  onToggleFinancial: panelLogic.onToggleFinancial,
  financialFilterCount: panelLogic.sectionCounts.financial,
  valuationRange: panelLogic.draft.valuationRange,
  onSetValuationRange: panelLogic.handleSetValuationRange,
  /* --- Fit & Ratings section --- */
  isFitRatingsOpen: panelLogic.expandedSections.fitAndRatings,
  onToggleFitRatings: panelLogic.onToggleFitRatings,
  fitRatingsFilterCount: panelLogic.sectionCounts.fitAndRatings,
  gprRange: panelLogic.draft.gprRange,
  onSetGprRange: panelLogic.handleSetGprRange,
  fitScoreRange: panelLogic.draft.fitScoreRange,
  onSetFitScoreRange: panelLogic.handleSetFitScoreRange,
  teamStyleFitRange: panelLogic.draft.teamStyleFitRange,
  onSetTeamStyleFitRange: panelLogic.handleSetTeamStyleFitRange,
  /* --- Performance section --- */
  isPerformanceOpen: panelLogic.expandedSections.performance,
  onTogglePerformance: panelLogic.onTogglePerformance,
  performanceFilterCount: panelLogic.sectionCounts.performance,
  carryingRange: panelLogic.draft.carryingRange,
  onSetCarryingRange: panelLogic.handleSetCarryingRange,
  dribblingRange: panelLogic.draft.dribblingRange,
  onSetDribblingRange: panelLogic.handleSetDribblingRange,
  passingRange: panelLogic.draft.passingRange,
  onSetPassingRange: panelLogic.handleSetPassingRange,
  shootingRange: panelLogic.draft.shootingRange,
  onSetShootingRange: panelLogic.handleSetShootingRange,
  /* --- Goalkeeper section --- */
  isGoalkeeperOpen: panelLogic.expandedSections.goalkeeper,
  onToggleGoalkeeper: panelLogic.onToggleGoalkeeper,
  goalkeeperFilterCount: panelLogic.sectionCounts.goalkeeper,
  gkPassingRange: panelLogic.draft.gkPassingRange,
  onSetGkPassingRange: panelLogic.handleSetGkPassingRange,
  gkShotStoppingRange: panelLogic.draft.gkShotStoppingRange,
  onSetGkShotStoppingRange: panelLogic.handleSetGkShotStoppingRange,
  gkPositioningRange: panelLogic.draft.gkPositioningRange,
  onSetGkPositioningRange: panelLogic.handleSetGkPositioningRange,
  /* --- Report Stats section --- */
  isReportStatsOpen: panelLogic.expandedSections.reportStats,
  onToggleReportStats: panelLogic.onToggleReportStats,
  reportStatsFilterCount: panelLogic.sectionCounts.reportStats,
  customMetrics: source.customMetrics,
  customStatRanges: panelLogic.draft.customStatRanges,
  onSetCustomStatRange: panelLogic.handleSetCustomStatRange,
});
